import {
  IRynReplayEnvelope,
  IRynReplayOfflineBatchRecord,
  IRynReplayOfflineStore,
  MemoryRynReplayOfflineStore,
  RYN_REPLAY_MAX_OFFLINE_BATCHES,
  RYN_REPLAY_OFFLINE_DB_NAME,
  RYN_REPLAY_OFFLINE_DB_VERSION,
  RYN_REPLAY_OFFLINE_STORE_NAME,
  trimOfflineBatchRecords,
} from '@ups/xplat/base/ryn-replay';

/**
 * The browser half of the replay offline buffer.
 *
 * Split from the store contract (which lives in `@ups/xplat/base/ryn-replay`
 * alongside the size-cap and eviction policy) because IndexedDB is browser-only
 * and the NativeScript recorder persists to the app cache directory instead.
 * The policy is what has to be identical across platforms; the storage engine
 * is what cannot be.
 */

type IdbFactory = typeof indexedDB;

/**
 * IndexedDB-backed store. Failures resolve empty / no-op so capture never
 * breaks the host app over a storage quota or private-mode restriction.
 */
export class IndexedDbRynReplayOfflineStore implements IRynReplayOfflineStore {
  constructor(
    private readonly dbName: string = RYN_REPLAY_OFFLINE_DB_NAME,
    private readonly indexedDb: IdbFactory | undefined = globalThis.indexedDB
  ) {}

  async enqueue(sessionId: string, batch: IRynReplayEnvelope[]): Promise<void> {
    if (batch.length === 0) {
      return;
    }
    const db = await this.open();
    if (!db) {
      return;
    }
    try {
      await this.idbRequest(
        db
          .transaction(RYN_REPLAY_OFFLINE_STORE_NAME, 'readwrite')
          .objectStore(RYN_REPLAY_OFFLINE_STORE_NAME)
          .add({
            sessionId,
            batch,
            enqueuedAt: Date.now(),
          })
      );
      await this.trim(db);
    } finally {
      db.close();
    }
  }

  async peekOldest(limit: number): Promise<IRynReplayOfflineBatchRecord[]> {
    const db = await this.open();
    if (!db) {
      return [];
    }
    try {
      const store = db
        .transaction(RYN_REPLAY_OFFLINE_STORE_NAME, 'readonly')
        .objectStore(RYN_REPLAY_OFFLINE_STORE_NAME);
      const all = (await this.idbRequest(store.getAll())) as Array<{
        id?: number;
        sessionId: string;
        batch: IRynReplayEnvelope[];
        enqueuedAt: number;
      }>;
      return all
        .filter((r) => typeof r.id === 'number')
        .map((r) => ({
          id: r.id as number,
          sessionId: r.sessionId,
          batch: r.batch,
          enqueuedAt: r.enqueuedAt,
        }))
        .sort((a, b) => a.id - b.id)
        .slice(0, Math.max(0, limit));
    } finally {
      db.close();
    }
  }

  async remove(id: number): Promise<void> {
    const db = await this.open();
    if (!db) {
      return;
    }
    try {
      await this.idbRequest(
        db
          .transaction(RYN_REPLAY_OFFLINE_STORE_NAME, 'readwrite')
          .objectStore(RYN_REPLAY_OFFLINE_STORE_NAME)
          .delete(id)
      );
    } finally {
      db.close();
    }
  }

  async count(): Promise<number> {
    const db = await this.open();
    if (!db) {
      return 0;
    }
    try {
      const n = await this.idbRequest(
        db
          .transaction(RYN_REPLAY_OFFLINE_STORE_NAME, 'readonly')
          .objectStore(RYN_REPLAY_OFFLINE_STORE_NAME)
          .count()
      );
      return typeof n === 'number' ? n : 0;
    } finally {
      db.close();
    }
  }

  private async trim(db: IDBDatabase): Promise<void> {
    const all = (await this.idbRequest(
      db
        .transaction(RYN_REPLAY_OFFLINE_STORE_NAME, 'readonly')
        .objectStore(RYN_REPLAY_OFFLINE_STORE_NAME)
        .getAll()
    )) as Array<{
      id?: number;
      sessionId: string;
      batch: IRynReplayEnvelope[];
      enqueuedAt: number;
    }>;
    const records = all
      .filter((r) => typeof r.id === 'number')
      .map((r) => ({
        id: r.id as number,
        sessionId: r.sessionId,
        batch: r.batch,
        enqueuedAt: r.enqueuedAt,
      }));
    const kept = new Set(
      trimOfflineBatchRecords(records, RYN_REPLAY_MAX_OFFLINE_BATCHES).map(
        (r) => r.id
      )
    );
    const toDelete = records.filter((r) => !kept.has(r.id)).map((r) => r.id);
    if (toDelete.length === 0) {
      return;
    }
    const writeStore = db
      .transaction(RYN_REPLAY_OFFLINE_STORE_NAME, 'readwrite')
      .objectStore(RYN_REPLAY_OFFLINE_STORE_NAME);
    for (const id of toDelete) {
      await this.idbRequest(writeStore.delete(id));
    }
  }

  private open(): Promise<IDBDatabase | undefined> {
    if (!this.indexedDb) {
      return Promise.resolve(undefined);
    }
    return new Promise((resolve) => {
      try {
        const req = this.indexedDb!.open(
          this.dbName,
          RYN_REPLAY_OFFLINE_DB_VERSION
        );
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(RYN_REPLAY_OFFLINE_STORE_NAME)) {
            db.createObjectStore(RYN_REPLAY_OFFLINE_STORE_NAME, {
              keyPath: 'id',
              autoIncrement: true,
            });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(undefined);
      } catch {
        resolve(undefined);
      }
    });
  }

  private idbRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error('IndexedDB request failed'));
    });
  }
}

export const createRynReplayOfflineStore = (): IRynReplayOfflineStore =>
  typeof globalThis.indexedDB === 'object' && globalThis.indexedDB
    ? new IndexedDbRynReplayOfflineStore()
    : new MemoryRynReplayOfflineStore();
